import { CaseAdmin, Clinic, User } from "pwapi";

import { useIntegrationValues } from "shared/hooks/useClinicFields";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { shallowLeftDiff } from "shared/utils/formUtils";

// EMR fields object has an unknown number of fields and return types.
type UnknownIntegrationFormFields = {
  [key: string]:
    | number
    | string
    | boolean
    | undefined
    | CaseAdmin.type
    | Clinic
    | Partial<User>;
};

// The integration form has some fields which are always available and expected.
export type IntegrationValuesFormFields = UnknownIntegrationFormFields & {
  caseType?: CaseAdmin.type;
  clinic?: Clinic;
  ordering_doctor?: Partial<User>;
  supervising_physician?: Partial<User>;
};

export const getInitialIntegrationValues = (
  adminCase: CaseAdminDetail
): IntegrationValuesFormFields => {
  return {
    clinic: adminCase?.patient?.clinic,
    ordering_doctor: adminCase?.ordering_doctor,
    ...useIntegrationValues({
      adminCaseOrValues: adminCase,
    }),
    supervising_physician: adminCase?.supervising_physician,
    ...useIntegrationValues({
      adminCaseOrValues: adminCase,
    }),
  };
};

export const submissionFieldsForIntegrations = (
  adminCase: CaseAdminDetail,
  values?: Partial<ReturnType<typeof getInitialIntegrationValues>>
) => {
  if (!values) values = getInitialIntegrationValues(adminCase);
  const integrationValues = useIntegrationValues({
    adminCaseOrValues: values,
    skipMRN: true,
    flatIntegration: true,
  });

  return {
    ordering_doctor_id: values?.ordering_doctor?.id || null,
    supervising_physician_id: values?.supervising_physician?.id || null,
    clinic_id: values?.clinic?.id || null,
    ...integrationValues,
  };
};

export const shallowIntegrationsComparison = (
  adminCase: CaseAdminDetail,
  values: Partial<ReturnType<typeof getInitialIntegrationValues>>
) => {
  const oldValues = submissionFieldsForIntegrations(adminCase);
  const newValues = submissionFieldsForIntegrations(adminCase, values);

  return {
    ...shallowLeftDiff<ReturnType<typeof submissionFieldsForIntegrations>>(
      oldValues,
      newValues
    ),
  };
};
